/**
 * Hook React pour la gestion des restrictions utilisateur
 * 
 * Ce hook fournit une interface simple pour vérifier les restrictions
 * d'un utilisateur et afficher les messages appropriés.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import userRestrictionService from '../services/userRestrictionService';

/**
 * Hook principal pour récupérer les restrictions de l'utilisateur
 * @param {Object} options - Options de configuration
 * @param {boolean} options.autoRefresh - Rafraîchir automatiquement (défaut: false)
 * @param {number} options.refreshInterval - Intervalle de rafraîchissement en ms (défaut: 60000)
 * @returns {Object} État des restrictions
 */
export function useUserRestrictions(options = {}) {
  const { autoRefresh = false, refreshInterval = 60000 } = options;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState({
    lastFetchAt: null,
    lastError: null
  });
  const [data, setData] = useState({
    isBanned: false,
    banType: null,
    banExpiresAt: null,
    banReason: null,
    restrictions: [],
    strikes: {
      currentPoints: 0,
      maxPoints: 10,
      progressPercent: 0,
      activeStrikes: 0,
      history: []
    },
    canAppeal: true
  });
  
  const intervalRef = useRef(null);
  
  const fetchRestrictions = useCallback(async () => {
    try {
      setLoading(true);
      setDebugInfo((prev) => ({
        ...prev,
        lastFetchAt: new Date().toISOString(),
        lastError: null
      }));
      const result = await userRestrictionService.getMyRestrictions();
      setData(result);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      const url = err?.config?.url;
      const method = err?.config?.method;
      const responseData = err?.response?.data;

      setDebugInfo((prev) => ({
        ...prev,
        lastFetchAt: new Date().toISOString(),
        lastError: {
          message: err?.message || 'Unknown error',
          status,
          method,
          url,
          responseData
        }
      }));

      const message =
        status ? `Erreur restrictions (HTTP ${status})` : (err.message || 'Erreur lors de la récupération des restrictions');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchRestrictions();
    
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchRestrictions, refreshInterval);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [fetchRestrictions, autoRefresh, refreshInterval]);
  
  return {
    loading,
    error,
    debugInfo,
    ...data,
    refresh: fetchRestrictions,
    hasActiveRestrictions: data.restrictions.length > 0 || data.isBanned
  };
}

/**
 * Hook pour vérifier le statut de ban uniquement
 * @returns {Object} Statut de ban
 */
export function useBanStatus() {
  const [loading, setLoading] = useState(true);
  const [isBanned, setIsBanned] = useState(false);
  const [banData, setBanData] = useState(null);
  
  useEffect(() => {
    const checkBan = async () => {
      try {
        const result = await userRestrictionService.checkBanStatus();
        setIsBanned(result.isBanned);
        setBanData(result);
      } catch (err) {
        console.error('useBanStatus error:', err);
        setIsBanned(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkBan();
  }, []);
  
  return { loading, isBanned, banData };
}

/**
 * Hook pour vérifier si une action spécifique est autorisée
 * @param {string} action - L'action à vérifier
 * @param {Object} options - Options supplémentaires (scope, scopeId)
 * @returns {Object} Résultat de la vérification
 */
export function useCanPerformAction(action, options = {}) {
  const { scope = null, scopeId = null, checkOnMount = true } = options;
  
  const [loading, setLoading] = useState(checkOnMount);
  const [allowed, setAllowed] = useState(true); // Optimiste par défaut
  const [restriction, setRestriction] = useState(null);
  const [reason, setReason] = useState(null);
  
  const checkAction = useCallback(async () => {
    if (!action) return;
    
    setLoading(true);
    try {
      const result = await userRestrictionService.checkRestriction(action, scope, scopeId);
      setAllowed(result.allowed);
      setRestriction(result.restriction);
      setReason(result.reason);
    } catch (err) {
      console.error('useCanPerformAction error:', err);
      // En cas d'erreur, on reste optimiste pour ne pas bloquer l'utilisateur
      setAllowed(true);
    } finally {
      setLoading(false);
    }
  }, [action, scope, scopeId]);
  
  useEffect(() => {
    if (checkOnMount) {
      checkAction();
    }
  }, [checkAction, checkOnMount]);
  
  return {
    loading,
    allowed,
    restricted: !allowed,
    restriction,
    reason,
    check: checkAction
  };
}

/**
 * Hook spécifique pour vérifier si l'utilisateur peut réserver
 * @returns {Object} Résultat
 */
export function useCanBook() {
  return useCanPerformAction('book');
}

/**
 * Hook spécifique pour vérifier si l'utilisateur peut créer une annonce
 * @returns {Object} Résultat
 */
export function useCanCreateListing() {
  return useCanPerformAction('create_listing');
}

/**
 * Hook spécifique pour vérifier si l'utilisateur peut envoyer des messages
 * @returns {Object} Résultat
 */
export function useCanSendMessage() {
  return useCanPerformAction('send_message');
}

/**
 * Hook spécifique pour vérifier si l'utilisateur peut laisser un avis
 * @returns {Object} Résultat
 */
export function useCanReview() {
  return useCanPerformAction('review');
}

/**
 * Hook pour déterminer si la bannière de restriction doit être affichée
 * @returns {Object} Données pour RestrictionBanner
 */
export function useRestrictionBanner() {
  const { loading, error, debugInfo, isBanned, banType, banReason, banExpiresAt, restrictions } = useUserRestrictions();
  
  // Déterminer le type d'alerte à afficher
  const alertType = isBanned ? 'ban' : ((restrictions?.length || 0) > 0 ? 'restriction' : null);
  
  // Compter les restrictions critiques
  const criticalRestrictions = (restrictions || []).filter(r => {
    const severity = userRestrictionService.getRestrictionSeverity(r.restriction_type);
    return severity === 'high' || severity === 'critical';
  });
  
  const shouldShow = isBanned || (restrictions?.length || 0) > 0;
  
  return {
    loading,
    error,
    debugInfo,
    shouldShow,
    alertType,
    isBanned,
    banType,
    banReason,
    banExpiresAt,
    daysRemaining: banExpiresAt ? userRestrictionService.getDaysRemaining(banExpiresAt) : null,
    restrictions: restrictions || [],
    criticalCount: criticalRestrictions.length,
    totalCount: restrictions?.length || 0
  };
}

export default {
  useUserRestrictions,
  useBanStatus,
  useCanPerformAction,
  useCanBook,
  useCanCreateListing,
  useCanSendMessage,
  useCanReview,
  useRestrictionBanner
};
